import { drawThumbnail, LOCATIONS } from '../world/LocationManager';

export interface OverlayCallbacks {
  /** Location chosen from the title screen or pause menu. */
  onSelectLocation(id: string): void;
  onResume(): void;
  onExport(): void;
  onImport(file: File): void;
}

const TOUCH_CONTROLS: [string, string][] = [
  ['Drag (one finger)', 'Look around'],
  ['Drag (two fingers)', 'Move — the world follows your fingers'],
  ['Pinch out / in', 'Walk forward / backward'],
  ['● button (hold)', 'Spray paint'],
  ['⤒ button', 'Jump'],
];

const CONTROLS: [string, string][] = [
  ['W A S D', 'Move'],
  ['Mouse', 'Look / aim'],
  ['Left mouse (hold)', 'Spray paint'],
  ['Mouse wheel', 'Spray size'],
  ['Shift', 'Sprint'],
  ['Space', 'Jump'],
  ['1 – 9', 'Palette colors'],
  ['V', 'First / third person'],
  ['P', 'Photo mode (saves a PNG)'],
  ['H', 'Help'],
  ['M', 'Mute'],
  ['Tab', 'Collapse HUD'],
  ['Esc', 'Pause menu'],
];

/** Title screen, pause menu, help, and confirm dialogs. */
export class Overlays {
  private titleEl: HTMLDivElement;
  private helpEl: HTMLDivElement;
  private confirmEl: HTMLDivElement;
  private confirmYes: (() => void) | null = null;
  private mode: 'title' | 'pause' = 'title';

  constructor(
    parent: HTMLElement,
    private readonly cb: OverlayCallbacks,
    isTouch = false,
  ) {
    // ----- title / pause -----
    this.titleEl = document.createElement('div');
    this.titleEl.className = 'overlay';
    this.titleEl.innerHTML = `
      <div class="overlay-panel">
        <div class="title-name">Snow Graffiti</div>
        <div class="title-tag">Fresh snow. A full can. Say something.</div>
        <p class="howto">
          Walk through quiet winter scenes and spray-paint the snow. Hold the left mouse
          button to spray — get close for dense color, stand back for soft wide strokes.
          Falling snow slowly buries your art, so keep painting or calm the sky.
        </p>
        <div class="location-grid"></div>
        <div class="menu-row">
          <button id="ov-resume" style="display:none">▶ Resume</button>
          <button id="ov-export" style="display:none">Export world (JSON)</button>
          <button id="ov-import" style="display:none">Import world</button>
          <button id="ov-help2">How to play</button>
        </div>
        <input id="ov-file" type="file" accept=".json,application/json" style="display:none" />
      </div>
    `;
    parent.appendChild(this.titleEl);

    const grid = this.titleEl.querySelector('.location-grid')!;
    for (const def of LOCATIONS) {
      const card = document.createElement('button');
      card.className = 'location-card';
      const canvas = document.createElement('canvas');
      drawThumbnail(canvas, def);
      card.appendChild(canvas);
      const name = document.createElement('div');
      name.className = 'loc-name';
      name.textContent = def.name;
      const desc = document.createElement('div');
      desc.className = 'loc-desc';
      desc.textContent = def.description;
      card.append(name, desc);
      card.addEventListener('click', () => this.cb.onSelectLocation(def.id));
      grid.appendChild(card);
    }

    this.titleEl.querySelector('#ov-resume')!.addEventListener('click', () => this.cb.onResume());
    this.titleEl.querySelector('#ov-export')!.addEventListener('click', () => this.cb.onExport());
    const fileInput = this.titleEl.querySelector<HTMLInputElement>('#ov-file')!;
    this.titleEl.querySelector('#ov-import')!.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      if (file) this.cb.onImport(file);
      fileInput.value = '';
    });
    this.titleEl.querySelector('#ov-help2')!.addEventListener('click', () => this.showHelp());

    // ----- help -----
    this.helpEl = document.createElement('div');
    this.helpEl.className = 'overlay hidden';
    const toRows = (list: [string, string][]) =>
      list.map(([k, d]) => `<kbd>${k}</kbd><span>${d}</span>`).join('');
    const touchSection = isTouch
      ? `<h3 style="margin-top:16px">Touch</h3><div class="help-grid">${toRows(TOUCH_CONTROLS)}</div>
         <h3 style="margin-top:16px">Keyboard &amp; mouse</h3>`
      : '';
    this.helpEl.innerHTML = `
      <div class="overlay-panel">
        <h2>How to play</h2>
        <p class="howto" style="text-align:left;margin:10px 0 0">
          Spray from up close for small, saturated marks; from farther away the spray gets
          wider, fainter and softer — like a real can. Snowfall gradually whitens your work.
        </p>
        ${touchSection}
        <div class="help-grid">${toRows(CONTROLS)}</div>
        <div class="menu-row"><button id="ov-help-close">Close</button></div>
      </div>
    `;
    parent.appendChild(this.helpEl);
    this.helpEl.querySelector('#ov-help-close')!.addEventListener('click', () => this.hideHelp());

    // ----- confirm -----
    this.confirmEl = document.createElement('div');
    this.confirmEl.className = 'overlay hidden';
    this.confirmEl.innerHTML = `
      <div class="overlay-panel">
        <h3 id="ov-confirm-msg"></h3>
        <div class="menu-row">
          <button id="ov-confirm-yes">Yes, reset</button>
          <button id="ov-confirm-no">Cancel</button>
        </div>
      </div>
    `;
    parent.appendChild(this.confirmEl);
    this.confirmEl.querySelector('#ov-confirm-yes')!.addEventListener('click', () => {
      this.confirmEl.classList.add('hidden');
      this.confirmYes?.();
      this.confirmYes = null;
    });
    this.confirmEl.querySelector('#ov-confirm-no')!.addEventListener('click', () => {
      this.confirmEl.classList.add('hidden');
      this.confirmYes = null;
    });
  }

  showTitle(): void {
    this.mode = 'title';
    this.applyMode();
    this.titleEl.classList.remove('hidden');
  }

  showPause(): void {
    this.mode = 'pause';
    this.applyMode();
    this.titleEl.classList.remove('hidden');
  }

  private applyMode(): void {
    const pause = this.mode === 'pause';
    const show = (id: string, visible: boolean) => {
      this.titleEl.querySelector<HTMLElement>(`#${id}`)!.style.display = visible ? '' : 'none';
    };
    show('ov-resume', pause);
    show('ov-export', pause);
    show('ov-import', pause);
    this.titleEl.querySelector('.title-tag')!.textContent = pause
      ? 'Paused — pick a new location or keep going.'
      : 'Fresh snow. A full can. Say something.';
  }

  hideMain(): void {
    this.titleEl.classList.add('hidden');
  }

  get mainVisible(): boolean {
    return !this.titleEl.classList.contains('hidden');
  }

  showHelp(): void {
    this.helpEl.classList.remove('hidden');
  }

  hideHelp(): void {
    this.helpEl.classList.add('hidden');
  }

  get helpVisible(): boolean {
    return !this.helpEl.classList.contains('hidden');
  }

  confirm(message: string, onYes: () => void): void {
    this.confirmEl.querySelector('#ov-confirm-msg')!.textContent = message;
    this.confirmYes = onYes;
    this.confirmEl.classList.remove('hidden');
  }

  get anyVisible(): boolean {
    return this.mainVisible || this.helpVisible || !this.confirmEl.classList.contains('hidden');
  }
}
